import { ComponentProps, CSSProperties } from 'react';

import classNames from 'classnames';

import { useTranslation } from '@/i18n';

import ButtonConfirmationMessage, { ButtonConfirmationMessageProps } from '../ButtonConfirmationMessage';
import Icon, { IconProps } from '../Icon';
import { Loader } from '../Loader';
import Popover, { PopoverProps } from '../Popover';
import Tooltip, { TooltipProps } from '../Tooltip';

import styles from './index.module.scss';

type ButtonStyle = CSSProperties & {
  '--button-bg-color'?: string;
  '--button-text-color'?: string;
};

export type ButtonProps = Omit<ComponentProps<'button'>, 'style' | 'type' | 'children'> & {
  text?: string;
  type?: 'primary' | 'secondary' | 'success' | 'danger' | 'transparent';
  icon?: IconProps['icon'];
  iconPosition?: 'start' | 'end';
  indicator?: boolean | string | number;
  tooltip?: Pick<TooltipProps, 'text' | 'side'>;
  confirmation?:
    | boolean
    | {
        enabled: boolean;
        props?: Partial<ButtonConfirmationMessageProps> & {
          side?: PopoverProps['side'];
        };
      };
  loading?: boolean;
  isSubmit?: boolean;
  style?: ButtonStyle;
};

export default function Button({
  type = 'primary',
  icon,
  iconPosition = 'start',
  indicator,
  tooltip,
  confirmation,
  loading,
  isSubmit,
  ...rest
}: ButtonProps) {
  const { t } = useTranslation('common');

  const { text, className, onClick, ...props } = rest;

  const Component = ({ onClick: _onClick = onClick }: Pick<ButtonProps, 'onClick'>) => (
    <button
      {...props}
      className={classNames(styles.button, type && styles[type], loading && styles.loading, className)}
      data-indicator-content={typeof indicator === 'boolean' ? undefined : indicator}
      data-indicator-show={Boolean(indicator)}
      onClick={_onClick}
      type={isSubmit ? 'submit' : 'button'}
    >
      {icon && iconPosition === 'start' && <Icon icon={icon} />}
      {text}
      {icon && iconPosition === 'end' && <Icon icon={icon} />}
      {loading && (
        <Loader className={styles.loader} invert={!['secondary', 'transparent'].includes(type)} size={20} strokeSize={2} isRelative />
      )}
    </button>
  );

  if (tooltip?.text) {
    return (
      <Tooltip side={tooltip.side} text={tooltip.text}>
        <Component />
      </Tooltip>
    );
  }

  const confirmationEnabled = typeof confirmation === 'boolean' ? confirmation : confirmation?.enabled;

  if (confirmationEnabled) {
    const confirmationProps = typeof confirmation === 'boolean' ? {} : confirmation!.props || {};
    const { text: confirmationText, side: confirmationSide, ...confirmationPropsRest } = confirmationProps;

    return (
      <Popover
        side={confirmationSide}
        content={(close) => (
          <ButtonConfirmationMessage
            onCancel={close}
            text={confirmationText || t('areYouSure')}
            onAccept={(e) => {
              onClick?.(e);
              close();
            }}
            {...confirmationPropsRest}
          />
        )}
      >
        {(trigger) => <Component onClick={trigger} />}
      </Popover>
    );
  }

  return <Component />;
}
